import React from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { AvailableProduct } from "../../types";
import { Badge, Button, Th, Td, Tr } from "../ui";

// Types
export interface FieldSchema {
  name: string;
  label: string;
  type:
  | "text"
  | "select"
  | "date"
  | "time"
  | "datetime-local"
  | "textarea"
  | "file"
  | "currency"
  | "tel"
  | "quantity"
  | "badge"
  | "checkbox"
  | "tiers"
  | "productList"
  | "inlineProductList";
  icon?: string;
  options?: string[];
  style?: "teal" | "dark";
  defaultValue?: string | number | boolean;
  accept?: string;
  hint?: string;
  prefix?: string;
  readOnly?: boolean;
  computed?: string;
  rows?: number;
  placeholder?: string;
}

interface FieldRendererProps {
  field: FieldSchema;
  value: any;
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  onShowProductModal: () => void;
  availableProducts?: AvailableProduct[];
  /** When true, renders all inputs as disabled/greyed out for view-only mode */
  readOnly?: boolean;
  /** Called when user clicks to preview a base64 image in view mode */
  onShowImagePreview?: (imageSrc: string) => void;
}

/** Shared control styling — the kit's TextInput look, minus its label wrapper. */
const control = (extra = "") =>
  "border-hairline/70 bg-surface focus:border-brand/70 w-full rounded-xl border px-4 py-2.5 " +
  "text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:outline-none " +
  `disabled:bg-elevated disabled:text-zinc-500 ${extra}`;

const compact = (extra = "") =>
  "border-hairline/70 bg-surface focus:border-brand/70 w-full rounded-lg border px-2.5 py-1.5 " +
  "text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:outline-none " +
  `disabled:bg-elevated disabled:text-zinc-500 ${extra}`;

/** Positions a leading bootstrap-icon inside a control, replacing InputGroup.Text. */
const IconWrap: React.FC<{
  icon?: string;
  prefix?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ icon, prefix, children, className = "" }) => (
  <div className={`relative ${className}`}>
    {(icon || prefix) && (
      <span className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-sm text-zinc-400">
        {icon ? <i className={`bi ${icon}`} /> : prefix}
      </span>
    )}
    {children}
  </div>
);

const InlineProductListInput: React.FC<{
  field: FieldSchema;
  value: any;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  availableProducts: AvailableProduct[];
  readOnly?: boolean;
}> = ({ field, value, setFormData, availableProducts, readOnly }) => {
  const items = value || [];

  const handleAddRow = () => {
    const newRow = { product: "", quantity: "", reason: "", expiry: "" };
    setFormData((prev) => ({ ...prev, [field.name]: [...items, newRow] }));
  };

  const handleRemoveRow = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setFormData((prev) => ({ ...prev, [field.name]: updatedItems }));
  };

  const handleItemChange = (
    index: number,
    fieldName: string,
    newValue: string
  ) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [fieldName]: newValue };
    setFormData((prev) => ({ ...prev, [field.name]: updatedItems }));
  };

  const LABEL = "text-xs font-medium tracking-wide text-zinc-500 uppercase";
  // 5/1/3/2/1 of the old 12-column Bootstrap grid, kept proportional.
  const GRID = "grid grid-cols-1 gap-2 md:grid-cols-[5fr_1fr_3fr_2fr_auto]";

  return (
    <div className="space-y-2">
      {/* Header Row with Labels + Add Button */}
      <div className={`${GRID} md:items-center`}>
        <span className={LABEL}>Product</span>
        <span className={`${LABEL} hidden md:block`}>Quantity</span>
        <span className={`${LABEL} hidden md:block`}>Reason</span>
        <span className={`${LABEL} hidden md:block`}>Expiry Date</span>
        {!readOnly && (
          <Button
            variant="outline"
            onClick={handleAddRow}
            aria-label="Add row"
            className="px-2.5 py-1.5"
          >
            <i className="bi bi-plus-lg" />
          </Button>
        )}
      </div>

      {/* Data Rows */}
      {items.map((item: any, idx: number) => (
        <div key={idx} className={`${GRID} md:items-center`}>
          <select
            className={compact()}
            value={item.product}
            onChange={(e) => handleItemChange(idx, "product", e.target.value)}
            disabled={readOnly}
          >
            <option value="">Select Product</option>
            {availableProducts.map((p, i) => (
              <option key={i} value={`${p.code} - ${p.name}`}>
                {p.code} - {p.name}
              </option>
            ))}
          </select>
          <input
            className={compact()}
            value={item.quantity}
            onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
            placeholder="Qty"
            disabled={readOnly}
          />
          <input
            className={compact()}
            value={item.reason}
            onChange={(e) => handleItemChange(idx, "reason", e.target.value)}
            placeholder="Reason"
            disabled={readOnly}
          />
          <input
            className={compact()}
            value={item.expiry}
            onChange={(e) => handleItemChange(idx, "expiry", e.target.value)}
            placeholder="Expiry"
            disabled={readOnly}
          />
          {!readOnly && (
            <Button
              variant="danger"
              onClick={() => handleRemoveRow(idx)}
              aria-label="Remove row"
              className="px-2.5 py-1.5"
            >
              <i className="bi bi-x-lg" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  formData,
  setFormData,
  onShowProductModal,
  availableProducts = [],
  readOnly = false,
  onShowImagePreview,
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value: inputValue } = e.target;
    setFormData((prev) => ({ ...prev, [name]: inputValue }));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, [fieldName]: e.target.files![0] }));
    }
  };

  const handleFileClear = (fieldName: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: null }));
  };

  const handleQuantityChange = (fieldName: string, delta: number) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: Math.max(1, (prev[fieldName] || 1) + delta),
    }));
  };

  const removeProduct = (index: number) => {
    const newProducts = [...(formData.products || [])];
    newProducts.splice(index, 1);
    setFormData((prev) => ({ ...prev, products: newProducts }));
  };

  const iconPad = field.icon ? "pl-10" : "";

  switch (field.type) {
    case "badge":
      return <Badge>{value || field.defaultValue}</Badge>;

    case "select":
      return (
        <IconWrap icon={field.icon}>
          <select
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={control(iconPad)}
            disabled={readOnly}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </IconWrap>
      );

    case "date":
      return (
        <IconWrap icon={field.icon}>
          <Flatpickr
            value={value}
            onChange={(dates: Date[]) => {
              // Store as YYYY-MM-DD to avoid UTC timezone shift (matches client format)
              if (dates[0]) {
                const d = dates[0];
                const pad = (n: number) => n.toString().padStart(2, "0");
                const localDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                setFormData((prev) => ({ ...prev, [field.name]: localDate }));
              } else {
                setFormData((prev) => ({ ...prev, [field.name]: "" }));
              }
            }}
            options={{ dateFormat: "Y-m-d", altInput: true, altFormat: "d/m/Y", allowInput: !readOnly, clickOpens: !readOnly }}
            className={control(iconPad)}
            placeholder="Select date"
            disabled={readOnly}
          />
        </IconWrap>
      );

    case "time":
      return (
        <IconWrap icon={field.icon}>
          <Flatpickr
            value={value}
            onChange={(dates: Date[]) => {
              const timeStr = dates[0]
                ? dates[0].toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
                : "";
              setFormData((prev) => ({ ...prev, [field.name]: timeStr }));
            }}
            options={{
              enableTime: true,
              noCalendar: true,
              dateFormat: "H:i",
              time_24hr: true,
              clickOpens: !readOnly,
            }}
            className={control(iconPad)}
            placeholder="Select time"
            disabled={readOnly}
          />
        </IconWrap>
      );

    case "textarea":
      const lineCount = Math.max(3, (value || "").split("\n").length);
      return (
        <div className="w-full">
          <div className="flex">
            {field.icon && (
              <div className="flex items-center pr-3">
                <i className="bi bi-quote text-xl text-zinc-400" />
              </div>
            )}
            {/* textarea-lines is also targeted by the print stylesheet in
                custom.css, which expands it so nothing is clipped on paper. */}
            <div className="textarea-lines border-hairline/70 bg-surface max-h-30 flex-1 overflow-y-auto rounded-xl border p-2">
              {Array.from({ length: lineCount }).map((_, idx) => {
                const lines = (value || "").split("\n");
                return (
                  <input
                    key={idx}
                    id={`${field.name}-line-${idx}`}
                    type="text"
                    value={lines[idx] || ""}
                    onChange={(e) => {
                      const newLines = [...lines];
                      newLines[idx] = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: newLines.join("\n"),
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const isLastLine = idx === lineCount - 1;
                        if (isLastLine) {
                          // Add new line
                          const lines = (value || "").split("\n");
                          lines.push("");
                          setFormData((prev) => ({
                            ...prev,
                            [field.name]: lines.join("\n"),
                          }));
                          // Focus new line after render
                          setTimeout(() => {
                            const nextInput = document.getElementById(
                              `${field.name}-line-${idx + 1}`
                            );
                            nextInput?.focus();
                          }, 10);
                        } else {
                          // Focus next existing line
                          const nextInput = document.getElementById(
                            `${field.name}-line-${idx + 1}`
                          );
                          nextInput?.focus();
                        }
                      } else if (
                        e.key === "Backspace" &&
                        lines[idx] === "" &&
                        idx >= 3
                      ) {
                        // Remove empty line (only for lines beyond initial 3)
                        e.preventDefault();
                        const newLines = [...lines];
                        newLines.splice(idx, 1);
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newLines.join("\n"),
                        }));
                        // Focus previous line
                        setTimeout(() => {
                          const prevInput = document.getElementById(
                            `${field.name}-line-${idx - 1}`
                          );
                          prevInput?.focus();
                        }, 10);
                      }
                    }}
                    className="border-hairline/60 w-full border-0 border-b bg-transparent px-1 py-1 text-sm text-zinc-900 focus:outline-none disabled:text-zinc-500"
                    disabled={readOnly}
                  />
                );
              })}
            </div>
          </div>
        </div>
      );

    case "file":
      const selectedFile = formData[field.name];
      const isBase64Image =
        typeof selectedFile === "string" && selectedFile.startsWith("data:image");

      if (readOnly && isBase64Image) {
        return (
          <button
            type="button"
            className="text-brand-strong hover:text-brand inline-flex items-center gap-1.5 text-sm transition-colors"
            onClick={() => onShowImagePreview?.(selectedFile)}
          >
            <i className="bi bi-image" />
            View Image
          </button>
        );
      }

      if (readOnly && !selectedFile) {
        return <span className="text-sm text-zinc-400">No image</span>;
      }

      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept={field.accept}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileChange(e, field.name)
              }
              className="file:bg-elevated hover:file:bg-hairline/60 w-full text-sm text-zinc-600 file:mr-3 file:rounded-lg file:border-0 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-zinc-700"
              disabled={readOnly}
            />
            {selectedFile && !readOnly && (
              <Button
                variant="danger"
                onClick={() => handleFileClear(field.name)}
                className="shrink-0 px-3 py-1.5 text-xs"
              >
                <i className="bi bi-x" />
                Clear
              </Button>
            )}
          </div>
          {selectedFile && selectedFile instanceof File && (
            <p className="text-xs font-medium text-zinc-700">
              Selected: {selectedFile.name}
            </p>
          )}
          {field.hint && <p className="text-xs text-zinc-500">{field.hint}</p>}
        </div>
      );

    case "currency":
      return (
        <IconWrap prefix={field.prefix || "£"}>
          <input
            type="number"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={control(
              `pl-9 ${field.readOnly || readOnly ? "bg-elevated" : ""}`
            )}
            step="0.01"
            readOnly={field.readOnly}
            disabled={readOnly}
          />
        </IconWrap>
      );

    case "tel":
      return (
        <IconWrap icon={field.icon}>
          <input
            type="tel"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={control(iconPad)}
            disabled={readOnly}
          />
        </IconWrap>
      );

    case "quantity":
      return (
        <div className="flex w-40 items-center gap-1">
          {!readOnly && (
            <Button
              variant="outline"
              onClick={() => handleQuantityChange(field.name, -1)}
              aria-label="Decrease quantity"
              className="shrink-0 px-2.5 py-2"
            >
              <i className="bi bi-dash" />
            </Button>
          )}
          <input
            type="number"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={control("px-2 py-2 text-center tabular-nums")}
            min={1}
            disabled={readOnly}
          />
          {!readOnly && (
            <Button
              variant="outline"
              onClick={() => handleQuantityChange(field.name, 1)}
              aria-label="Increase quantity"
              className="shrink-0 px-2.5 py-2"
            >
              <i className="bi bi-plus" />
            </Button>
          )}
        </div>
      );

    case "checkbox":
      return (
        <input
          type="checkbox"
          name={field.name}
          checked={!!value}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, [field.name]: e.target.checked }))
          }
          className="border-hairline accent-brand size-4 rounded"
          disabled={readOnly}
        />
      );

    case "tiers":
      const tiersData =
        formData[field.name] ||
        Array.from({ length: field.rows || 8 }, () => ({
          size: "",
          message: "",
        }));
      return (
        <div className="border-hairline/70 w-full overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th className="w-16">Tier</Th>
                <Th className="w-32">Size</Th>
                <Th>Message</Th>
              </tr>
            </thead>
            <tbody>
              {tiersData.map((tier: any, index: number) => (
                <Tr key={index}>
                  <Td className="text-zinc-500 tabular-nums">{index + 1}</Td>
                  <Td>
                    <input
                      type="text"
                      value={tier.size}
                      onChange={(e) => {
                        const newTiers = [...tiersData];
                        newTiers[index] = {
                          ...newTiers[index],
                          size: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newTiers,
                        }));
                      }}
                      className={compact()}
                      disabled={readOnly}
                    />
                  </Td>
                  <Td>
                    <input
                      type="text"
                      value={tier.message}
                      onChange={(e) => {
                        const newTiers = [...tiersData];
                        newTiers[index] = {
                          ...newTiers[index],
                          message: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newTiers,
                        }));
                      }}
                      className={compact()}
                      disabled={readOnly}
                    />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "productList":
      const products = formData[field.name] || [];
      return (
        <div className="border-hairline/70 w-full overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Product</Th>
                <Th>Size</Th>
                <Th>Quantity</Th>
                {!readOnly && (
                  <Th className="w-16 text-center">
                    <Button
                      variant="outline"
                      onClick={onShowProductModal}
                      className="px-2 py-1"
                      title="Add Product"
                    >
                      <i className="bi bi-plus-lg" />
                    </Button>
                  </Th>
                )}
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td
                    colSpan={readOnly ? 4 : 5}
                    className="px-4 py-8 text-center text-sm text-zinc-500"
                  >
                    No products added yet
                  </td>
                </tr>
              ) : (
                products.map((p: any, idx: number) => (
                  <Tr key={idx}>
                    <Td className="font-mono text-xs text-zinc-500">{p.code}</Td>
                    <Td className="font-medium text-zinc-900">{p.name}</Td>
                    <Td>{p.size}</Td>
                    <Td className="tabular-nums">{p.quantity}</Td>
                    {!readOnly && (
                      <Td>
                        <Button
                          variant="danger"
                          onClick={() => removeProduct(idx)}
                          aria-label="Remove product"
                          className="px-2 py-1"
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );

    case "inlineProductList":
      return (
        <InlineProductListInput
          field={field}
          value={value}
          setFormData={setFormData}
          availableProducts={availableProducts}
          readOnly={readOnly}
        />
      );

    case "text":
    default:
      return (
        <IconWrap icon={field.icon}>
          <input
            type="text"
            name={field.name}
            value={value}
            onChange={handleInputChange}
            className={control(iconPad)}
            placeholder={field.placeholder}
            disabled={readOnly}
          />
        </IconWrap>
      );
  }
};

export default FieldRenderer;
